import { useMemo, useState } from 'react'

import useRegionsListQuery from 'common/api/queries/useRegionsListQuery'
import Button from 'common/components/button'
import EmptyState from 'common/components/emptyState'
import ErrorPage from 'common/components/error/ErrorPage'
import Loader from 'common/components/loaders/Loader'
import PageContainer from 'common/components/PageContainer'
import PageTitle from 'common/components/PageTitle'
import { Add as AddIcon } from 'common/icons'

import useGetLanguageRulesQuery from '../api/queries/useGetLanguageRulesQuery'

import AddLanguageRulesDrawer from './components/languageRulesForm/AddLanguageRulesDrawer'
import LanguageRulesList from './components/languageRulesList'
import SearchInput from './components/SearchInput'

const Rules = () => {
  const [searchValue, setSearchValue] = useState('')

  const { data: regions } = useRegionsListQuery()
  const { data: languageRules, isLoading, isError } = useGetLanguageRulesQuery()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const languageRulesFiltered = useMemo(
    () =>
      languageRules?.filter((rule) => {
        const lowerCaseSearchValue = searchValue.toLowerCase()
        const region = regions?.find(
          (region) =>
            region.language_settings?.iso_language === rule.isoLanguage
        )

        return (
          (lowerCaseSearchValue === 'all'
            ? rule.isoLanguage === 'all'
            : region?.name.toLowerCase().includes(lowerCaseSearchValue)) ||
          rule.rule.toLowerCase().includes(lowerCaseSearchValue)
        )
      }) ?? [],
    [languageRules, regions, searchValue]
  )

  const getState = () => {
    if (isLoading) {
      return 'loading'
    } else if (isError) {
      return 'error'
    } else if (languageRules?.length ?? 0 > 1) {
      return 'data'
    } else {
      return 'empty'
    }
  }

  const state = getState()

  return (
    <PageContainer className="max-w-318 sm:mx-auto pt-10 px-6 justify-start">
      <div className="flex justify-between items-center">
        <PageTitle title="Language rules" />
        <div className="flex gap-6">
          <Button
            data-cy="add-language-rule-button"
            variant="primary"
            onClick={() => setIsDrawerOpen(true)}
            prefixIcon={
              <AddIcon
                isDefaultColor={false}
                size={4}
                className="text-base-700"
              />
            }
          >
            RULE
          </Button>
          {state === 'data' && <SearchInput onSearch={setSearchValue} />}
        </div>
      </div>
      <div className="text-base-700 mt-2 mb-10">
        A set of rules specific to a language variety, curated to ensure
        grammatically accurate and fluent output.
      </div>
      <AddLanguageRulesDrawer
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
      />
      {
        {
          loading: <Loader />,
          error: <ErrorPage />,
          data: <LanguageRulesList languageRules={languageRulesFiltered} />,
          empty: (
            <EmptyState
              title="No Language rules currently"
              description="Create Language rules to fix grammatical issues in native dialects."
              className="h-full"
              action={
                <Button variant="primary" onClick={() => setIsDrawerOpen(true)}>
                  CREATE YOUR FIRST RULE
                </Button>
              }
            />
          ),
        }[state]
      }
    </PageContainer>
  )
}

export default Rules
